import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView, animate } from "framer-motion";
import { ArrowRight, ArrowUpRight, ShieldCheck, Clock3, MapPin, Sparkles, Building2, SprayCan, Snowflake, Leaf, Recycle } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { usePageMeta } from "@/utils/seo";
import Marquee from "@/components/Marquee";

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1621831337128-35676ca30868?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBnbGFzcyUyMGNvbW1lcmNpYWwlMjBidWlsZGluZyUyMGZhY2FkZSUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODc5NDIwODB8MA&ixlib=rb-4.1.0&q=80&w=1920",
  fassade: "https://images.unsplash.com/photo-1482449609509-eae2a7ea42b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxhcmNoaXRlY3R1cmFsJTIwZ2xhc3MlMjBjbGVhbiUyMGJ1aWxkaW5nfGVufDB8fHx8MTc4ODUzMDI4MHww&ixlib=rb-4.1.0&q=75&w=1600",
};

const SERVICES = [
  { nr: "01", slug: "gebaeudeunterhalt", title: "Gebäudeunterhalt", icon: Building2, text: "Laufende Betreuung Ihrer Liegenschaft — Hauswartservice, Kontrollgänge und Koordination aller Arbeiten.", testid: "service-card-gebaeudeunterhalt" },
  { nr: "02", slug: "unterhaltsreinigung", title: "Unterhaltsreinigung", icon: SprayCan, text: "Regelmässige Reinigung von Büros, Treppenhäusern und Gemeinschaftsflächen. Diskret, gründlich, planbar.", testid: "service-card-reinigung" },
  { nr: "03", slug: "winterservice", title: "Winterservice", icon: Snowflake, text: "Schneeräumung und Streudienst ab der ersten Flocke — sieben Tage die Woche, rund um die Uhr.", testid: "service-card-winterservice" },
  { nr: "04", slug: "spezialreinigung", title: "Spezialreinigung", icon: Sparkles, text: "Umzug mit Abgabegarantie, Bauend- und Grundreinigung sowie Fassadenreinigung — bis ins letzte Detail.", testid: "service-card-spezialreinigung" },
  { nr: "05", slug: "gartenarbeiten", title: "Gartenarbeiten", icon: Leaf, text: "Pflege und Unterhalt Ihrer Grünflächen — Rasen, Hecken, Beete und saisonale Gartenarbeiten.", testid: "service-card-gartenarbeiten" },
  { nr: "06", slug: "entsorgung", title: "Entsorgung", icon: Recycle, text: "Fachkundiger Rückbau, Entkernung und effiziente, sachgerechte Entsorgung.", testid: "service-card-entsorgung" },
];

const MANIFESTO = [
  { nr: "01", title: "Präzision", text: "Wir arbeiten nach klaren Checklisten und festen Qualitätsstandards. Jeder Einsatz wird dokumentiert — was wir zusagen, halten wir ein." },
  { nr: "02", title: "Verlässlichkeit", text: "Feste Ansprechpartner, fixe Einsatzpläne und ein Pikettdienst, der auch am Sonntagmorgen um fünf ausrückt, wenn der Winter zuschlägt." },
  { nr: "03", title: "Regionalität", text: "Aus Mühlau, für die Kantone Aargau, Luzern, Zürich und Zug. Kurze Wege bedeuten schnelle Reaktionszeiten und persönliche Nähe zu jedem Objekt." },
];

const TESTIMONIALS = [
  { quote: "Die Umzugsreinigung wurde vom Vermieter ohne eine einzige Bemerkung abgenommen. Absolut stressfrei.", name: "M. Brunner", role: "Privatkundin, Mühlau" },
  { quote: "Seit HRG unser Treppenhaus und die Aussenanlagen betreut, bekommen wir nur noch Komplimente von Mietern.", name: "A. Frei", role: "Verwaltung, Wohnüberbauung Sins" },
  { quote: "Der Winterdienst war auch bei der grossen Schneefront im Januar um 5 Uhr morgens vor Ort. Vorbildlich.", name: "R. Keller", role: "Geschäftsführer, Gewerbebetrieb Beinwil" },
];

function Counter({ to, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, { duration: 1.8, ease: "easeOut", onUpdate: (v) => setVal(Math.round(v)) });
    return () => controls.stop();
  }, [inView, to]);
  return <span ref={ref}>{val.toLocaleString("de-CH")}{suffix}</span>;
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-end overflow-hidden bg-[#0A111E]">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Moderne Glasfassade eines Geschäftsgebäudes" fetchpriority="high" decoding="async" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0A111E]/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A111E] via-transparent to-transparent" />
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-8 lg:px-16 pb-20 pt-40">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="font-tech text-xs uppercase tracking-[0.3em] text-[#9db8e8] mb-6 flex items-center gap-3"
        >
          <span className="inline-block h-[2px] w-10 bg-[#5882C7]" />
          HRG Service GmbH — Mühlau AG, Schweiz
        </motion.p>
        <h1 data-testid="hero-headline" className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight text-white leading-[1.05]">
          <MaskedLines
            lines={[
              <>Sauberkeit ist</>,
              <>kein Zufall.</>,
              <span className="text-[#5882C7]">Sie ist Handwerk.</span>,
            ]}
          />
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.75 }}
          className="mt-6 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
        >
          Gebäudeunterhalt, Reinigung, Winterservice, Spezial- und Fassadenreinigung sowie Gartenarbeiten — professionell betreut aus Mühlau, in den Kantonen Aargau, Luzern, Zürich und Zug.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <Link
            to="/kontakt"
            data-testid="hero-cta-primary"
            className="group inline-flex items-center gap-2 rounded-full bg-[#3B62A5] px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#5882C7]"
          >
            Offerte anfragen
            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <Link
            to="/leistungen"
            data-testid="hero-cta-secondary"
            className="glass-dark group inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-white/20"
          >
            Leistungen entdecken
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function Stats() {
  const stats = [
    { value: 50, suffix: "+", label: "Betreute Objekte & Kunden" },
    { value: 6, suffix: "", label: "Fachbereiche unter einem Dach" },
    { value: 24, suffix: "h", label: "Winterdienst-Bereitschaft" },
    { value: 100, suffix: "%", label: "Abgabegarantie bei Umzug" },
  ];
  return (
    <section className="border-b border-slate-200 bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 py-14 grid grid-cols-2 lg:grid-cols-4 gap-10">
        {stats.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.08}>
            <p className="text-4xl sm:text-5xl font-bold tracking-tight text-[#3B62A5]"><Counter to={s.value} suffix={s.suffix} /></p>
            <p className="mt-2 text-sm text-slate-500">{s.label}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function ServicesGrid() {
  return (
    <section className="bg-mesh">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 py-24 lg:py-32">
        <Reveal>
          <p className="font-tech text-xs uppercase tracking-[0.25em] text-[#3B62A5] font-semibold mb-4">Unsere Fachbereiche</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0A111E] max-w-2xl">
            Sechs Disziplinen. Ein Anspruch: <span className="text-[#3B62A5]">makellos.</span>
          </h2>
        </Reveal>
        <div data-testid="services-grid" className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.slug} delay={i * 0.07} className="h-full">
                <Link
                  to={`/leistungen/${s.slug}`}
                  data-testid={s.testid}
                  className="glass-card block h-full rounded-3xl p-7 transition-[box-shadow,border-color] duration-300 hover:border-[#3B62A5]/40 hover:shadow-[0_20px_50px_rgba(59,98,165,0.15)]"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#E8F0FE]/80">
                    <Icon className="h-6 w-6 text-[#3B62A5]" />
                  </span>
                  <h3 className="mt-5 text-xl font-semibold tracking-tight text-[#0A111E]">{s.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.text}</p>
                </Link>
              </Reveal>
            );
          })}
        </div>
        <Reveal delay={0.15} className="mt-10">
          <Link
            to="/leistungen"
            data-testid="services-more-button"
            className="glass-soft group inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium text-slate-600 transition-colors duration-200 hover:border-[#3B62A5] hover:text-[#3B62A5]"
          >
            Alle Leistungen im Detail
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section className="border-t border-slate-200 bg-mesh">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <Reveal>
              <p className="font-tech text-xs uppercase tracking-[0.25em] text-[#3B62A5] font-semibold mb-4">Unser Anspruch</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0A111E]">Drei Prinzipien, an denen wir uns messen lassen.</h2>
              <div className="mt-8 flex flex-wrap gap-3">
                <span className="glass-soft inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium text-slate-600"><ShieldCheck className="h-4 w-4 text-[#3B62A5]" /> Versichert & geprüft</span>
                <span className="glass-soft inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium text-slate-600"><Clock3 className="h-4 w-4 text-[#3B62A5]" /> Fixe Einsatzpläne</span>
                <span className="glass-soft inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium text-slate-600"><MapPin className="h-4 w-4 text-[#3B62A5]" /> AG · LU · ZH · ZG</span>
              </div>
            </Reveal>
          </div>
        </div>
        <div className="lg:col-span-7 space-y-0">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.nr} delay={i * 0.08}>
              <div className="group flex gap-6 border-t border-slate-200 py-10 transition-colors duration-300 hover:bg-[#F8FAFC]">
                <span className="font-tech text-3xl sm:text-4xl font-semibold text-[#3B62A5]/25 transition-colors duration-300 group-hover:text-[#3B62A5]">{m.nr}</span>
                <div>
                  <h3 className="text-xl sm:text-2xl font-semibold tracking-tight text-[#0A111E]">{m.title}</h3>
                  <p className="mt-3 text-base text-slate-600 leading-relaxed max-w-lg">{m.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="border-t border-slate-200 bg-mesh">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 py-24 lg:py-32">
        <Reveal>
          <p className="font-tech text-xs uppercase tracking-[0.25em] text-[#3B62A5] font-semibold mb-4">Stimmen unserer Kunden</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0A111E]">Vertrauen, das wächst.</h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.1}>
              <blockquote className="glass-card flex h-full flex-col justify-between rounded-3xl p-8">
                <p className="font-editorial text-lg leading-relaxed text-[#0A111E]">«{t.quote}»</p>
                <footer className="mt-8">
                  <p className="text-sm font-semibold text-[#3B62A5]">{t.name}</p>
                  <p className="text-xs text-slate-500 mt-1">{t.role}</p>
                </footer>
              </blockquote>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaBand() {
  return (
    <section className="relative overflow-hidden bg-[#0A111E]">
      <img src={IMAGES.fassade} alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-25" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A111E] via-[#0A111E]/85 to-[#3B62A5]/40" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 py-24 lg:py-32">
        <Reveal>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white max-w-3xl leading-[1.1]">
            Ihr Objekt verdient <span className="text-[#5882C7]">Bestform.</span>
          </h2>
          <p className="mt-5 max-w-xl text-base text-slate-300 leading-relaxed">
            Erzählen Sie uns von Ihrer Liegenschaft — wir melden uns innert 24 Stunden mit einer unverbindlichen Offerte.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/kontakt" className="group inline-flex items-center gap-2 rounded-full bg-[#3B62A5] px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#5882C7]" data-testid="cta-band-button">
              Jetzt Kontakt aufnehmen
              <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <a href="tel:+41782238087" className="glass-dark inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-white/20" data-testid="cta-band-phone">
              +41 78 223 80 87
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  usePageMeta({
    title: "HRG Service GmbH | Gebäudeunterhalt & Reinigung Mühlau AG",
    description: "Gebäudeunterhalt, Reinigung, Winterservice, Spezialreinigung, Gartenarbeiten und Entsorgung — professionell aus Mühlau für die Kantone Aargau, Luzern, Zürich und Zug.",
    path: "/",
  });
  return (
    <main>
      <Hero />
      <Stats />
      <ServicesGrid />
      <Marquee />
      <Manifesto />
      <Testimonials />
      <CtaBand />
    </main>
  );
}
